import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Assignee display + case-link helpers shared by OperationalExcellenceHub
 * (Overview/L1/L2/L3 boards) and the Top Customers pages -- same rendering
 * rules, one implementation so a fix in one doesn't silently miss the other.
 *
 * Consolidated from two previously-separate, near-duplicate copies. Where the
 * two differed, this keeps the more complete behavior for both callers:
 *  - displayAssigneeName: keeps the trailing-parenthetical strip and the
 *    email -> display-name humanization steps (the Hub's version had them,
 *    the Top Customers copy didn't -- an email/parenthetical assignee would
 *    have rendered unhumanized there).
 *  - CaseIdLink: keeps absoluteHttpUrl validation (the Hub's version had it,
 *    Top Customers didn't -- a non-http href would have rendered as a live
 *    link there) and keeps the stopPropagation() click guard (Top Customers'
 *    version had it, for rows that are themselves clickable; harmless no-op
 *    for the Hub's non-clickable rows).
 *  - AssigneeCell / CaseIdLink: accept a `compact` prop so each page keeps
 *    its existing sizing instead of forcing one page's density on the other.
 */

const MUTED = '#666666';
const INK = '#1A1A1A';

export const displayAssigneeName = (assignedTo) => {
  if (assignedTo && typeof assignedTo === 'object') {
    return displayAssigneeName(assignedTo.displayName || assignedTo.uniqueName || assignedTo.name || '');
  }
  let name = String(assignedTo || '').trim();
  if (!name) return '';
  name = name.replace(/^.*\\/, '').trim();
  name = name.replace(/\s*<[^>]*>\s*/g, ' ').trim();
  name = name.replace(/\s*\([^)]*\)\s*$/g, '').trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) {
    name = name
      .split('@')[0]
      .replace(/[._+]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const parts = name.split(',').map((s) => s.trim()).filter(Boolean);
  return parts.length === 2 ? `${parts[1]} ${parts[0]}` : name;
};

export const isUnassigned = (assignedTo) => !assignedTo || !String(assignedTo).trim();

export const ownerFromAssignedTo = (assignedTo) => {
  const display = displayAssigneeName(assignedTo);
  if (!display) return { name: 'Unassigned', initials: '?' };
  const initials = display
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return { name: display, initials: initials || '?' };
};

/** Only render a link for a genuine absolute http(s) URL -- guards against relative/malformed hrefs. */
export const absoluteHttpUrl = (url) => {
  const raw = String(url || '').trim();
  return /^https?:\/\/\S+/i.test(raw) ? raw : '';
};

export const AssigneeCell = ({ assignedTo, color, bg, emptyLabel = '—', compact = false }) => {
  const fontSize = compact ? '0.75rem' : '0.8125rem';
  if (isUnassigned(assignedTo)) {
    return <Typography sx={{ color: MUTED, fontSize: compact ? '0.75rem' : '0.75rem' }}>{emptyLabel}</Typography>;
  }
  const person = ownerFromAssignedTo(assignedTo);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', width: compact ? undefined : 'max-content' }}>
      <Box
        sx={{
          width: compact ? 22 : 24,
          height: compact ? 22 : 24,
          borderRadius: '50%',
          bgcolor: bg,
          color,
          fontSize: compact ? '0.6rem' : '0.625rem',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {person.initials}
      </Box>
      <Typography noWrap sx={{ fontSize, color: INK }}>
        {person.name}
      </Typography>
    </Box>
  );
};

export const CaseIdLink = ({ value, href, color = INK, compact = false, onClick }) => {
  const fontSize = compact ? '0.75rem' : '0.8125rem';
  if (!value) {
    return <Typography sx={{ color: MUTED, fontSize }}>—</Typography>;
  }
  const linkSx = {
    fontWeight: 600,
    color,
    whiteSpace: 'nowrap',
    fontSize,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  };
  // In-app action (e.g. open case details panel) — not an external full-page navigation.
  if (typeof onClick === 'function') {
    return (
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }}
        sx={{
          appearance: 'none',
          border: 'none',
          background: 'none',
          p: 0,
          m: 0,
          fontFamily: 'inherit',
          ...linkSx
        }}
      >
        {value}
      </Box>
    );
  }
  const url = absoluteHttpUrl(href);
  if (!url) {
    return (
      <Typography sx={{ fontWeight: 600, color, whiteSpace: 'nowrap', fontSize }}>
        {value}
      </Typography>
    );
  }
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={linkSx}
    >
      {value}
    </Box>
  );
};
